import { PageContainer } from "../../../../../components/pageContainer/PageContainer";
import { LegacyCard } from "../../../../../components/card/LegacyCard";
import { Button, H3, H6 } from "@sellar-io/components";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Icon, SpinnerLegacy } from "../../../../../components";
import { useDashboardContext } from "../../../../../components/dashboard/Dashboard.shared";
import { NavigationBar } from "../../../../../components/navigation/NavigationBar";
import {
  EDIT_PRODUCT,
  PRODUCT_LIST,
  PRODUCT_LIST_CATEGORY,
} from "../../../../../constants/routes";
import { useParamAsNumber } from "../../../../../hooks/useParamAsNumber";
import { useSupplierProduct } from "../../../../../hooks/api/v2/product/useSupplierProduct";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import { AlertContainer } from "../../../../../components/alert/AlertContainer";
import { CategoryImage } from "../../../../stockList/categoryPage/categorySidePanel/CategoryImage";
import { ActiveProductCategoryDialog } from "../../ActiveProductCategoryDialog/ActiveProductCategoryDialog";
import CreateProductItemDialog from "../../CreateNewProductItemDialog/CreateProductItemDialog";
import { BadgeItemModal } from "../BadgeItemModal";
import { ProductCategoryHeader } from "../ProductCategoryHeader";
import { ProductCategoryItemV2 } from "./ProductCategoryItemV2";
import { ProductCategorySharePricingButton } from "./ProductCategorySharePricingButton";
import { ProductTemplateList } from "../../CreateNewProductItemDialog/components/ProductTemplateList";
import { ProductTemplateType } from "@sellar-io/api-v2/types";
import { StorefrontAssignProductTagModal } from "../../../../stockList/categoryPage/Storefront/StorefrontDialogs/StorefrontAssignProductTagModal";

export const ProductCategoryV2 = () => {
  const productId = useParamAsNumber(PRODUCT_LIST_CATEGORY, "productId");
  const { business } = useSellarCurrentBusiness();
  const { isTwoPanel } = useDashboardContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNewProduct = searchParams.get("new") === "true";

  if (!business?.Supplier) throw new Error("Must be a supplier");

  const productQuery = useSupplierProduct(productId);

  const [addItemModalActive, setAddItemModalActive] = useState(false);
  const [itemKey, setItemKey] = useState<number | undefined>(0);

  const openDialog = () => {
    setItemKey((prev) => (prev ?? 0) + 1);
    setAddItemModalActive(true);
  };

  const [storeFrontTagModalActive, setStorefrontTagModalActive] =
    useState(false);
  const [skipTemplateSelection, setSkipTemplateSelection] = useState(false);
  const [productTemplate, setProductTemplate] = useState<
    ProductTemplateType | undefined
  >();

  const [active, setActive] = useState<boolean>(false);
  const [badgeItemActive, setBadgeItemActive] = useState<boolean>(false);

  const productHeroImages = productQuery.data?.ProductImages.map(
    (ProductImage) => ProductImage.url,
  );
  const productHeroImagesLength = productHeroImages?.length ?? 0;

  const shouldOpenStorefrontTagModal = Boolean(
    productQuery.data &&
    productQuery.data.ProductStorefrontTag.length === 0 &&
    isNewProduct,
  );
  const [previousShouldOpenTagModal, setPreviousShouldOpenTagModal] =
    useState(false);
  if (shouldOpenStorefrontTagModal !== previousShouldOpenTagModal) {
    setPreviousShouldOpenTagModal(shouldOpenStorefrontTagModal);
    if (shouldOpenStorefrontTagModal) {
      // If no storefront tag (group) is assigned, open the modal to assign one
      setStorefrontTagModalActive(true);
    }
  }

  return (
    <>
      <NavigationBar
        title={
          productQuery.isSuccess && productQuery.data.name
            ? productQuery.data.name
            : "Loading..."
        }
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={isTwoPanel ? undefined : () => navigate(PRODUCT_LIST)}
      />
      {productQuery.isLoading ? (
        <SpinnerLegacy />
      ) : !productQuery.isSuccess ? (
        <div>Something went wrong</div>
      ) : (
        <PageContainer className={"flex flex-col gap-4 md:mx-4"}>
          <div className="p-4 pb-0">
            <AlertContainer
              type={productQuery.data.active ? "success" : undefined}
              className={"flex w-full flex-row items-center justify-between"}
            >
              <div className="blue-700 flex flex-row">
                <div>
                  {productQuery.data.active ? (
                    <Icon
                      size={0.8}
                      icon={"CHECK_CIRCLE"}
                      className={"mr-2 text-emerald-800"}
                    />
                  ) : (
                    <Icon size={0.8} icon={"INFORMATION"} className={"mr-2"} />
                  )}
                </div>
                <div className="flex flex-col">
                  <span className={"text-black"}>
                    This product is currently:{" "}
                    <strong>
                      {productQuery.data.active ? "ACTIVE" : "INACTIVE"}
                    </strong>
                  </span>
                </div>
              </div>
              <Button
                color={"tertiary"}
                size={"small"}
                className={"p-0 hover:bg-transparent"}
                onClick={() => setActive(true)}
              >
                <Icon size={0.6} icon={"PENCIL"} />
                Edit
              </Button>
            </AlertContainer>
          </div>
          <LegacyCard className={"flex flex-col gap-4 p-4 sm:flex-row"}>
            <div className={"relative basis-2/5"}>
              <CategoryImage
                imageUrl={
                  productQuery.data.ProductImages[0]?.url ??
                  productQuery.data.heroImageUrl ??
                  business.Supplier.imageUrl ??
                  ""
                }
              />
              {productHeroImagesLength > 1 && (
                <span className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-zinc-50 px-2 py-1 text-sm leading-none font-semibold">
                  <Icon size={0.7} icon="IMAGE_OUTLINE" />+
                  {productHeroImagesLength}
                </span>
              )}
              <Button
                size="small"
                color="secondary"
                onClick={() => navigate(EDIT_PRODUCT.href({ productId }))}
                className="absolute bottom-2 left-2"
              >
                <Icon icon="PENCIL" size={1} />
              </Button>
            </div>
            <div className={"flex basis-3/5 flex-col gap-4"}>
              <ProductCategoryHeader
                imageUrl={
                  productQuery.data.imageUrl ?? business.Supplier.imageUrl ?? ""
                }
                name={productQuery.data.name}
                kind={productQuery.data.kind}
                abv={productQuery.data.abv}
              />
              <H6 className={"line-clamp-3"}>
                {productQuery.data.description}
              </H6>
              <div className="flex flex-row gap-2">
                <ProductCategorySharePricingButton productId={productId} />
                <Button
                  color={"secondary"}
                  onClick={() => navigate(EDIT_PRODUCT.href({ productId }))}
                >
                  <Icon icon={"PENCIL"} />
                  Edit Details
                </Button>
              </div>
            </div>
          </LegacyCard>
          <div className={"flex items-center justify-between px-4"}>
            <div>
              <H3>{productQuery.data.Products.length} items</H3>
            </div>
            <div className={"flex gap-1"}>
              <Button
                color={"secondary"}
                onClick={() => setBadgeItemActive(true)}
              >
                <Icon icon={"PLUS"} /> Badge
              </Button>
              <Button
                onClick={() => {
                  setProductTemplate(undefined);
                  setSkipTemplateSelection(false);
                  openDialog();
                }}
              >
                <Icon icon={"PLUS"} /> Item
              </Button>
            </div>
          </div>
          <div className={"flex flex-col gap-2"}>
            {productQuery.data.Products.length === 0 ? (
              <ProductTemplateList
                launchProductCreate={(template) => {
                  setProductTemplate(template);
                  setSkipTemplateSelection(true);
                  openDialog();
                }}
              />
            ) : (
              productQuery.data.Products.map((product) => (
                <div key={product.id} id={product.id.toString()}>
                  <ProductCategoryItemV2
                    product={product}
                    parentProduct={productQuery.data}
                  />
                </div>
              ))
            )}
          </div>
          <ActiveProductCategoryDialog
            active={active}
            productId={productQuery.data.id}
            onDismiss={() => setActive(false)}
            productActive={productQuery.data.active}
          />
        </PageContainer>
      )}
      <BadgeItemModal
        onDismiss={() => setBadgeItemActive(false)}
        active={badgeItemActive}
      />
      {productQuery.data && (
        <CreateProductItemDialog
          key={itemKey}
          parentProduct={productQuery.data}
          active={addItemModalActive}
          defaultTemplate={productTemplate}
          skipTemplateSelection={skipTemplateSelection}
          onDismiss={(productId?: number) => {
            // product id is returned if product is created, scrolls into view of product
            if (productId) {
              setTimeout(() => {
                const targetElement = document.getElementById(
                  productId.toString(),
                );
                if (targetElement) {
                  targetElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              }, 500);
            }
            setAddItemModalActive(false);
          }}
        />
      )}
      <StorefrontAssignProductTagModal
        active={storeFrontTagModalActive}
        onDismiss={() => setStorefrontTagModalActive(false)}
        productId={productId}
        productName={productQuery.data?.name ?? ""}
      />
    </>
  );
};
